'use client';

/**
 * PriorityBadge Component
 *
 * A pill-shaped badge for displaying priority levels.
 * Inspired by modern dashboard priority indicators.
 */

import Chip, { type ChipProps } from '@mui/material/Chip';
import { alpha, styled } from '@mui/material/styles';
import { RecommendationSeverity } from '@ops-matrix/shared-types';
import { forwardRef } from 'react';
import { baseColors } from '../../theme/palette';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

/** @deprecated Use RecommendationSeverity from @ops-matrix/shared-types */
export type PriorityLevel = `${RecommendationSeverity}`;

export interface PriorityBadgeProps extends Omit<ChipProps, 'color' | 'label'> {
  /**
   * Priority level
   */
  priority: PriorityLevel;

  /**
   * Custom label (defaults to priority name capitalized)
   */
  label?: string;

  /**
   * Size variant
   * @default 'small'
   */
  size?: 'small' | 'medium';

  /**
   * Use filled variant (more prominent)
   * @default true
   */
  filled?: boolean;
}

// ============================================================================
// Priority Configuration
// ============================================================================

interface PriorityConfig {
  label: string;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
}

const getPriorityConfig = (priority: PriorityLevel, filled: boolean): PriorityConfig => {
  const configs: Record<PriorityLevel, PriorityConfig> = {
    critical: {
      label: 'Critical',
      backgroundColor: filled ? baseColors.error[500] : alpha(baseColors.error[500], 0.15),
      textColor: filled ? baseColors.neutral[0] : baseColors.error[400],
      borderColor: filled ? baseColors.error[500] : alpha(baseColors.error[500], 0.3),
    },
    high: {
      label: 'High',
      backgroundColor: filled ? baseColors.warning[500] : alpha(baseColors.warning[500], 0.15),
      textColor: filled ? baseColors.neutral[900] : baseColors.warning[400],
      borderColor: filled ? baseColors.warning[500] : alpha(baseColors.warning[500], 0.3),
    },
    medium: {
      label: 'Medium',
      backgroundColor: filled ? alpha(baseColors.neutral[500], 0.8) : alpha(baseColors.neutral[500], 0.15),
      textColor: filled ? baseColors.neutral[0] : baseColors.neutral[400],
      borderColor: filled ? alpha(baseColors.neutral[500], 0.8) : alpha(baseColors.neutral[500], 0.3),
    },
    low: {
      label: 'Low',
      backgroundColor: filled ? alpha(baseColors.neutral[600], 0.6) : alpha(baseColors.neutral[600], 0.1),
      textColor: filled ? baseColors.neutral[200] : alpha(baseColors.neutral[400], 0.8),
      borderColor: filled ? alpha(baseColors.neutral[600], 0.6) : alpha(baseColors.neutral[600], 0.2),
    },
    info: {
      label: 'Info',
      backgroundColor: filled ? baseColors.info[500] : alpha(baseColors.info[500], 0.15),
      textColor: filled ? baseColors.neutral[0] : baseColors.info[400],
      borderColor: filled ? baseColors.info[500] : alpha(baseColors.info[500], 0.3),
    },
  };

  return configs[priority];
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledChipProps {
  $config: PriorityConfig;
  $filled: boolean;
}

const StyledChip = styled(
  Chip,
  transientOptions,
)<StyledChipProps>(({ theme, $config, $filled }) => {
  const isMaterial = theme.designGuideline === 'material';

  return {
    backgroundColor: $config.backgroundColor,
    color: $config.textColor,
    border: `1px solid ${$config.borderColor}`,
    borderRadius: isMaterial ? 8 : 6, // MD3 uses 8px for small components
    fontWeight: isMaterial ? 500 : 600,
    fontSize: '0.75rem',
    letterSpacing: '0.02em',
    textTransform: 'capitalize' as const,
    height: 'auto',

    '& .MuiChip-label': {
      padding: '4px 8px',
    },

    '&.MuiChip-sizeMedium': {
      fontSize: '0.75rem',
      '& .MuiChip-label': {
        padding: '6px 12px',
      },
    },

    // Hover effect
    '&:hover': {
      backgroundColor: $filled ? $config.backgroundColor : alpha($config.backgroundColor, 0.25),
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export const PriorityBadge = forwardRef<HTMLDivElement, PriorityBadgeProps>(
  ({ priority, label, size = 'small', filled = true, ...props }, ref) => {
    const config = getPriorityConfig(priority, filled);
    const displayLabel = label || config.label;

    return <StyledChip ref={ref} label={displayLabel} size={size} $config={config} $filled={filled} {...props} />;
  },
);

PriorityBadge.displayName = 'PriorityBadge';

export default PriorityBadge;
